import { useInfiniteQuery } from "@tanstack/react-query";

import {
  ContactsAPI,
  GetContactsQuery,
  GetContactsResponse,
} from "./contacts.api";

const contactsQueryKeys = {
  list: (q?: GetContactsQuery) => ["contacts", "list", q],
  infiniteList: (q?: GetContactsQuery) => ["contacts", "infinite-list", q],
  detail: (contactId: number) => ["contacts", contactId],
  organizations: () => ["contacts", "organizations"],
  types: () => ["contacts", "types"],
};

// GET /contacts falls back to the service's 10,000 row limit when no page_size
// is sent, which defeats the point of paging. Keep infinite scroll bounded.
const DEFAULT_PAGE_SIZE = 100;

const useGetInfiniteContacts = (
  query: GetContactsQuery = {},
  options?: { enabled?: boolean }
) => {
  // `page` comes from react-query's pageParam, so drop any caller-supplied one:
  // leaving it in would both fragment the query key and get overwritten below.
  const { page, ...scope } = query;

  const contactsQuery: GetContactsQuery = {
    ...scope,
    page_size: scope.page_size ?? DEFAULT_PAGE_SIZE,
    search: scope.search === "" ? undefined : scope.search,
  };

  return useInfiniteQuery<GetContactsResponse>({
    queryKey: contactsQueryKeys.infiniteList(contactsQuery),
    queryFn: async ({ pageParam }) => {
      const res = await ContactsAPI.get({
        ...contactsQuery,
        page: pageParam as number,
      });

      // The API layer never rejects: HTTP/network failures resolve as
      // { success: false, error } (connection.js handleError). Convert that to a
      // real error here — otherwise react-query records a success and the list
      // renders a silent empty result with the retry UI unreachable.
      if (!res || (res as any).success === false || !Array.isArray(res.data)) {
        throw new Error("Failed to load contacts");
      }

      return res;
    },
    getNextPageParam: (lastPage) => lastPage?.nextPage ?? null,
    getPreviousPageParam: (firstPage) => {
      if (!firstPage?.currentPage) return null;
      if (firstPage.currentPage - 1 === 0) return null;
      return firstPage.currentPage - 1;
    },
    initialPageParam: 1,
    placeholderData: (data) => data,
    ...options,
  });
};

export const ContactsQuery = {
  queryKeys: contactsQueryKeys,
  useGetInfiniteContacts,
};
